import React, { useMemo, useState } from 'react';
import styles from './AddEvidencePopup.module.css';
import Button from '../Button/Button';
import { ChevronIcon, FolderIcon } from '../../../Routes/Library/LibraryIcons';
import { findLibraryFolder, LibraryFolder, LibraryPortfolio } from '../../../Types/Library/Library';
import LibraryExplorerTree from './LibraryExplorerTree';

type LibraryFolderPickerProps = {
  portfolios: LibraryPortfolio[];
  saving: boolean;
  fileCount: number;
  onCancel: () => void;
  onConfirm: (portfolioId: string, folderId: string) => void;
};

const isSharedAccess = (access?: string) => {
  const value = (access ?? '').trim().toLowerCase();
  return value === 'public' || value === 'global';
};

const folderCrumbsOf = (folders: LibraryFolder[] | undefined, folderId: string) => {
  const crumbs: { id: string; title: string }[] = [];
  const walk = (nodes: LibraryFolder[] | undefined): boolean => {
    for (const folder of nodes ?? []) {
      if (folder.Id === folderId) {
        crumbs.push({ id: folder.Id, title: folder.Title });
        return true;
      }
      if (walk(folder.folders)) {
        crumbs.unshift({ id: folder.Id, title: folder.Title });
        return true;
      }
    }
    return false;
  };
  walk(folders);
  return crumbs;
};

const LibraryFolderPicker: React.FC<LibraryFolderPickerProps> = ({
  portfolios,
  saving,
  fileCount,
  onCancel,
  onConfirm,
}) => {
  const destinations = useMemo(() => {
    const editable = portfolios.filter((portfolio) => portfolio.canUpload !== false);
    const viewOnly = portfolios.filter((portfolio) => portfolio.canUpload === false);
    return [...editable, ...viewOnly];
  }, [portfolios]);
  const viewOnlyIds = useMemo(
    () => new Set(destinations.filter((portfolio) => portfolio.canUpload === false).map((portfolio) => portfolio.id)),
    [destinations]
  );
  const first = destinations.find((portfolio) => portfolio.canUpload !== false) ?? destinations[0];
  const firstFolder = first?.folders?.[0];
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const open = new Set<string>();
    if (first?.id) open.add(`p:${first.id}`);
    if (firstFolder?.Id) open.add(`f:${firstFolder.Id}`);
    return open;
  });
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(first?.id ?? '');
  const [selectedFolderId, setSelectedFolderId] = useState(firstFolder?.Id ?? '');

  const selectedPortfolio = destinations.find((portfolio) => portfolio.id === selectedPortfolioId);
  const selectedIsViewOnly = !!selectedPortfolio && selectedPortfolio.canUpload === false;
  const crumbs = useMemo(
    () => (selectedPortfolio && selectedFolderId ? folderCrumbsOf(selectedPortfolio.folders, selectedFolderId) : []),
    [selectedPortfolio, selectedFolderId]
  );
  const visibleFolders = useMemo(() => {
    if (!selectedPortfolio) return [];
    if (!selectedFolderId) return selectedPortfolio.folders ?? [];
    return findLibraryFolder(selectedPortfolio.folders, selectedFolderId)?.folders ?? [];
  }, [selectedPortfolio, selectedFolderId]);

  const openPortfolio = (portfolioId: string) => {
    setSelectedPortfolioId(portfolioId);
    setSelectedFolderId('');
    setExpanded((current) => new Set(current).add(`p:${portfolioId}`));
  };

  const openFolder = (portfolioId: string, folderId: string) => {
    setSelectedPortfolioId(portfolioId);
    setSelectedFolderId(folderId);
    setExpanded((current) => {
      const next = new Set(current);
      next.add(`p:${portfolioId}`);
      next.add(`f:${folderId}`);
      return next;
    });
  };

  const destinationLabel = selectedPortfolio
    ? [selectedPortfolio.title, ...crumbs.map((crumb) => crumb.title)].join(' / ')
    : 'Choose a folder';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Save to library</h2>
        <p className={styles.subtitle}>
          Choose the portfolio and folder for {fileCount === 1 ? 'this file' : `these ${fileCount} files`}.
        </p>
      </div>

      {destinations.length === 0 ? (
        <p className={styles.emptyFiles}>
          You don&apos;t have access to any portfolios.
        </p>
      ) : (
        <div className={styles.explorer}>
          <LibraryExplorerTree
            portfolios={destinations}
            expanded={expanded}
            setExpanded={setExpanded}
            selectedPortfolioId={selectedPortfolioId}
            selectedFolderId={selectedIsViewOnly ? '' : selectedFolderId}
            onOpenPortfolio={openPortfolio}
            onOpenFolder={openFolder}
            viewOnlyIds={viewOnlyIds}
          />
          <div className={styles.explorerMain}>
            <div className={styles.crumbs}>
              <button type="button" onClick={() => selectedPortfolio && openPortfolio(selectedPortfolio.id)}>
                {selectedPortfolio?.title ?? 'Portfolio'}
              </button>
              {crumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  <ChevronIcon size={12} />
                  {index === crumbs.length - 1 ? (
                    <span>{crumb.title}</span>
                  ) : (
                    <button type="button" onClick={() => selectedPortfolio && openFolder(selectedPortfolio.id, crumb.id)}>
                      {crumb.title}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
            {selectedIsViewOnly ? (
              <p className={styles.emptyFiles}>
                You can view this portfolio, but you don&apos;t have permission to add files to it.
              </p>
            ) : visibleFolders.length === 0 ? (
              <p className={styles.emptyFiles}>
                {selectedFolderId
                  ? 'This folder will receive the file.'
                  : 'This portfolio has no folders yet.'}
              </p>
            ) : (
              <ul className={styles.explorerList}>
                {visibleFolders.map((folder) => (
                  <li key={folder.Id}>
                    <button
                      type="button"
                      className={styles.explorerFolder}
                      onClick={() => selectedPortfolio && openFolder(selectedPortfolio.id, folder.Id)}
                    >
                      <span className={styles.folderGlyph}><FolderIcon size={16} /></span>
                      <span>{folder.Title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {selectedIsViewOnly ? (
        <p className={styles.saveHint}>
          View only. You can open this portfolio, but you can&apos;t save files into it.
        </p>
      ) : selectedPortfolio && !isSharedAccess(selectedPortfolio.access) ? (
        <p className={styles.saveHint}>
          This is a private portfolio. Only its members will be able to open the file.
        </p>
      ) : null}

      <div className={styles.footer}>
        <p className={styles.footerHint}>
          {selectedIsViewOnly ? "View only — you can't save files here" : `Saving to: ${destinationLabel}`}
        </p>
        <div className={styles.footerActions}>
          <Button
            size="Small"
            colour="#FFF"
            textColour="#000"
            border="rgba(0,0,0,0.15)"
            clickHandler={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            colour="#24394F"
            clickHandler={() => selectedPortfolio && selectedFolderId && onConfirm(selectedPortfolio.id, selectedFolderId)}
            disabled={saving || selectedIsViewOnly || !selectedPortfolio || !selectedFolderId}
          >
            {saving ? 'Saving…' : 'Save to library'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LibraryFolderPicker;
